import React from 'react';
import {Alert, Button, Grid, Snackbar, Typography} from "@mui/material";
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import {salesInvoiceApi} from "../../../backend/SalesInvoiceApi";
import {formatDate} from "../../../utils/DataFormat";
import {customersStore} from "../../../store/CustomersStore";
import {GetSalesInvoiceDetailsDto} from "../../../model/invoice/sales/GetSalesInvoiceDetailsDto";
import {calculateLines, InvoiceCalculationResult} from "../../../utils/InvoiceCalculator";
import {invoiceDataStore} from "../../../store/InvoiceDataStore";
import {InvoiceTotals} from "../../../components/invoice/InvoiceTotals";
import TwoSidedPage from "../../../components/TwoSidedPage";
import {selectedCompanyStore} from "../../../store/SelectedCompany";
import PrintIcon from "@mui/icons-material/Print";
import {Email} from "@mui/icons-material";
import LoadingButton from "../../../components/button/LoadingButton";
import {InvoiceLockedReason} from "../../../model/invoice/sales/InvoiceLockedReason";
import SendSalesInvoiceDialog from "./SendSalesInvoiceDialog";
import {PartyComponent} from "../../../components/invoice/PartyComponent";
import {InvoiceLinesTable} from "../../../components/invoice/InvoiceLinesTable";
import {Trans} from "react-i18next";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {invoiceWizardState} from "../../../store/InvoiceWizardState";
import moment from "moment";
import {NavigateFunction} from "react-router/lib/hooks";
import {routes} from "../../../main/Routes";
import {decodeBase64} from "../../../utils/ReadFile";
import {saveFile} from "../../../utils/FileDownloadUtils";


interface IProps {
    invoiceId: string,
    navigate: NavigateFunction;
}

interface IState {
    loading: boolean,
    data?: {
        invoice: GetSalesInvoiceDetailsDto,
        calculationResults: InvoiceCalculationResult
    },
    openSendEmailDialog: boolean,
    showEmailWasSentNotification: boolean,
}

export default class SalesInvoice extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            ...this.loadingState(),
            openSendEmailDialog: false,
            showEmailWasSentNotification: false
        };
    }

    private loadingState() {
        return {
            loading: true,
        };
    }

    async componentDidMount() {
        if (selectedCompanyStore.isSelected) {
            const [invoice] = await Promise.all([salesInvoiceApi.getInvoice(this.props.invoiceId), customersStore.refresh(), invoiceDataStore.refresh()]);

            this.setState({
                loading: false,
                data: {
                    invoice,
                    calculationResults: calculateLines(invoiceDataStore.taxes, invoice.invoiceLines)
                },
            })
        }
    }


    render() {
        const {loading, data} = this.state;
        return (
                <>
                    <LoaderWithCompanySelected loading={loading}>
                        {data && this.renderInvoice()}
                    </LoaderWithCompanySelected>
                </>
        );
    }

    private renderInvoice = () => {
        const {invoice, calculationResults} = this.state.data!!

        return <>
            <TwoSidedPage
                    leftPanelSize={2}
                    leftPanel={this.renderInvoiceActions(invoice)}
                    rightPanelSize={10}
                    rightPanel={this.renderInvoiceContent(invoice, calculationResults)}
            />
        </>
    }

    private renderInvoiceContent(invoice: GetSalesInvoiceDetailsDto, calculationResults: InvoiceCalculationResult) {
        return (
                <Grid container spacing={1}>
                    <Grid container item xs={12}>
                        <Grid item xs={6} md={8} style={{paddingLeft: "1.5rem"}}>
                            {this.renderInvoiceInfo(invoice)}
                        </Grid>
                        <Grid item xs={6} md={4}>
                            {this.renderClientInfo(invoice)}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={this.splitterStyle}>
                        <InvoiceLinesTable lines={invoice.invoiceLines}
                                           calculationResults={calculationResults} currencyCode={invoice.currencyCode}
                                           articleDescResolver={invoiceDataStore.findArticleDesc}
                                           uomNameResolver={invoiceDataStore.findUomName}
                                           taxNameResolver={invoiceDataStore.findTaxName}
                        />
                    </Grid>
                    <Grid xs={12} item container justifyContent="flex-end" style={{...this.splitterStyle, marginRight: "0.5rem"}}>
                        <Grid item xs={4}>
                            <InvoiceTotals calculationResult={calculationResults} currency={invoice.currencyCode}/>
                        </Grid>
                    </Grid>
                    {invoice.comment && <Grid item container xs={12} style={{...this.splitterStyle, marginLeft: "0.5rem", marginRight: "0.5rem", marginBottom: "1rem"}}>
                        <Typography>
                            <pre style={{margin: 0}}>{invoice.comment}</pre>
                        </Typography>
                    </Grid>}
                </Grid>
        );
    }

    private renderInvoiceInfo(invoice: GetSalesInvoiceDetailsDto) {
        return <>
            <Grid container spacing={1}>
                <Grid item xs={2}><Typography variant="h6"><Trans i18nKey="single_invoice.inv_number">Invoice #</Trans></Typography></Grid>
                <Grid item xs={10}><Typography variant="h6">{invoice.invoiceNo}</Typography></Grid>

                <Grid item xs={2}><Typography variant="subtitle1"><Trans i18nKey="single_invoice.date">Invoice date</Trans></Typography></Grid>
                <Grid item xs={10}><Typography variant="subtitle1">
                    {formatDate(invoice.documentDate)}</Typography></Grid>

                <Grid item xs={2}><Typography variant="subtitle1"><Trans i18nKey="single_invoice.due">Due date</Trans></Typography></Grid>
                <Grid item xs={10}><Typography variant="subtitle1">{formatDate(invoice.dueDate)}</Typography></Grid>
            </Grid>
        </>
    }

    private renderClientInfo(invoice: GetSalesInvoiceDetailsDto) {
        const customer = customersStore.find(invoice.customerId)!!;
        return <PartyComponent party={customer} type="to"/>;
    }

    private renderInvoiceActions(invoice: GetSalesInvoiceDetailsDto) {
        const {openSendEmailDialog, showEmailWasSentNotification} = this.state;
        return <>
            <Grid container spacing={1}>
                {invoice.lockReason === InvoiceLockedReason.INVOICE_CREATED_EXTERNALLY && <Grid item xs={12}>
                    <Alert severity="warning">
                        <Trans i18nKey="single_invoice.conversion_alert">
                            Invoice was created externally.<br/>
                            PDF generation will convert it to internal format
                        </Trans>
                    </Alert>
                </Grid>}
                <Grid item xs={12}>
                    <LoadingButton fullWidth variant={"contained"} startIcon={<PrintIcon/>}
                                   onClick={this.downloadInvoice(invoice)}>
                        <Trans i18nKey="single_invoice.download_invoice">Download invoice</Trans>
                    </LoadingButton>
                </Grid>
                <Grid item xs={12}>
                    <Button fullWidth onClick={this.openSendEmailDialog} variant={"contained"} color={"primary"}
                            startIcon={<Email/>}>
                        <Trans i18nKey="single_invoice.send_via_email">Send via email</Trans>
                    </Button>
                    {openSendEmailDialog &&
                            <SendSalesInvoiceDialog open={openSendEmailDialog} invoice={invoice}
                                                    onSend={this.emailSent} onClose={this.closeSendEmailDialog}
                            />}
                    <Snackbar open={showEmailWasSentNotification} autoHideDuration={5000}
                              onClose={this.closeEmailSnackBar}>
                        <Alert onClose={this.closeEmailSnackBar} severity="success">
                            <Trans i18nKey="single_invoice.email_sent_successfully">
                                Email was sent successfully!
                            </Trans>
                        </Alert>
                    </Snackbar>
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton fullWidth variant="contained" startIcon={<FileCopyIcon/>}
                                   onClick={this.copyInvoice(invoice)}>
                        <Trans i18nKey="single_invoice.duplicate_invoice">Duplicate invoice</Trans>
                    </LoadingButton>
                </Grid>
            </Grid>
        </>;
    }

    private emailSent = () => {
        this.closeSendEmailDialog();
        this.setState({
            showEmailWasSentNotification: true
        })
    }

    private closeEmailSnackBar = () => {
        this.setState({
            showEmailWasSentNotification: false
        })
    }

    private closeSendEmailDialog = () => {
        this.setState({openSendEmailDialog: false});
    }

    private openSendEmailDialog = () => {
        this.setState({openSendEmailDialog: true});
    }

    private downloadInvoice = (invoice: GetSalesInvoiceDetailsDto) => async () => {
        const data = await salesInvoiceApi.downloadInvoice(invoice.id);
        const base64Response = decodeBase64(data.data);
        const customer = customersStore.find(invoice.customerId)!!;
        saveFile(invoice.invoiceNo + "_" + customer.name + ".pdf", await base64Response)
    }

    private copyInvoice = (invoice: GetSalesInvoiceDetailsDto) => async () => {
        invoiceWizardState.resetInvoiceWizardState()
        invoiceWizardState.selectedCustomerId = invoice.customerId;
        invoiceWizardState.invoiceDate = moment(invoice.documentDate);
        invoiceWizardState.dueDate = moment(invoice.dueDate);
        invoiceWizardState.selectedCurrency = invoice.currencyCode;
        invoiceWizardState.invoiceLines.replace(invoice.invoiceLines);
        invoiceWizardState.invoiceLines.forEach(inv => {
            if (inv.item.uomCode) {
                //See com.nola.services.invoice.SalesInvoiceService.convertLines for the reason of the hack
                inv.item.code = inv.item.code.replace(`+${inv.item.uomCode}`, "")
            }
        })
        invoiceWizardState.comment = invoice.comment;
        this.props.navigate(routes.newSalesInvoice)
    }

    private splitterStyle = {paddingTop: "1rem", marginTop: "1rem", borderTop: "solid 1px"};
}